import { ChipModal, ChipModalBody, ChipModalField, ChipModalFooter, ChipModalHeader, ChipSelect } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useAccess } from "~/core/access";
import { ApiError } from "~/core/api";
import { pick, useI18n, useT } from "~/core/i18n";
import presets from "../../../../packages/content/media-presets.json";
import {
	clipLengths,
	extraKnobs,
	fileUrl,
	type GeneratedFile,
	generateMedia,
	type MediaOperation,
	mediaKey,
	mediaOptions,
	readOperation,
} from "./api";

export type Modality = "image" | "speech" | "video";

const MODALITIES: Modality[] = ["image", "speech", "video"];
/** The lengths a clip is offered at when its model publishes none. The gateway caps a request at 60s. */
const SECONDS = [5, 8, 10, 15];
/** Names for the model knobs the dialog offers; anything else shows its key. */
const KNOBS: Record<string, [string, string]> = {
	resolution: ["Resolution", "分辨率"],
	quality: ["Quality", "画质"],
	generate_audio: ["Generate audio", "生成音频"],
	background: ["Background", "背景"],
	output_format: ["Image format", "图片格式"],
};
const NAMES: Record<Modality, [string, string]> = {
	image: ["Image", "图片"],
	speech: ["Narration", "配音"],
	video: ["Video", "视频"],
};
const BRIEFS: Record<Modality, [string, string]> = {
	image: ["Image description", "图片描述"],
	speech: ["Narration text", "配音文本"],
	video: ["Video description", "视频描述"],
};
const ROLES: Record<Modality, string> = { image: "image", speech: "audio", video: "video" };
const PREFIX: Record<Modality, string> = { image: "image/", speech: "audio/", video: "video/" };

/** Which generators an upload control's accepted types leave room for. */
export function generatable(accept: string | undefined): Modality[] {
	const kinds: [string, Modality][] = [
		["image/", "image"],
		["video/", "video"],
		["audio/", "speech"],
	];
	return kinds.filter(([mime]) => accept?.includes(mime)).map(([, modality]) => modality);
}

export interface GeneratorProps {
	brandId: string;
	modalities?: Modality[];
	disabled?: boolean;
	/** Offered when the caller can take the file — a page block, an attachment. */
	onUse?: (fileId: string, alt: string, contentType: string) => void;
	/** What to start from when the file is being made again: the words it already carries. */
	initialPrompt?: string;
	/** The model and knobs to open on, when a setting is being edited. */
	initial?: MediaSettings;
}

/**
 * A model and the knobs chosen for it, without a prompt: what a chat message
 * carries so the Agent's generation for that modality runs on exactly these.
 */
export interface MediaSettings {
	modality: Modality;
	model: string;
	preset?: string;
	seconds?: number;
	voice?: string;
	params?: Record<string, string | number | boolean>;
}

type Generator = ReturnType<typeof useGenerator>;

const RUNNING = ["waiting", "running", "queued"];

function useGenerator({ brandId, modalities, disabled, onUse, initialPrompt, initial }: GeneratorProps, open: boolean) {
	const { locale } = useI18n();
	const t = useT();
	const { editor } = useAccess();
	const tr = (en: string, zh: string) => pick(locale, [en, zh]);
	const kinds = modalities?.length ? modalities : MODALITIES;
	const [modality, setModality] = useState<Modality>(kinds[0]);
	const [prompt, setPrompt] = useState(initialPrompt ?? "");
	const [picked, setModel] = useState(initial?.model ?? "");
	const [preset, setPreset] = useState(initial?.preset ?? "landscape");
	const [seconds, setSeconds] = useState(initial?.seconds ?? SECONDS[0]);
	const [voice, setVoice] = useState(initial?.voice ?? "");
	const [knobs, setKnobs] = useState<Record<string, unknown>>(initial?.params ?? {});
	const [operationId, setOperationId] = useState("");
	// One id per generation, so a retried submit joins the operation it
	// already started instead of paying for a second one.
	const requestId = useRef(crypto.randomUUID());
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const options = useQuery({
		queryKey: [...mediaKey(brandId), "options"],
		queryFn: () => mediaOptions(brandId),
		enabled: open,
	});
	const models = options.data?.models.filter((m) => m.modality === modality) ?? [];
	// Nothing picked yet is the console's default for this kind.
	const model = picked || (models.find((m) => m.default)?.id ?? "");
	const selected = models.find((m) => m.id === model);
	const lengths = clipLengths(selected, SECONDS);
	// A length the new model does not make falls to its shortest.
	const length = lengths.includes(seconds) ? seconds : lengths[0];
	function chooseModel(id: string) {
		setModel(id);
		setVoice("");
		setKnobs({});
	}
	const operation = useQuery<MediaOperation>({
		queryKey: ["operation", operationId],
		queryFn: () => readOperation(operationId),
		enabled: open && !!operationId,
		refetchInterval: (q) => (!q.state.data || RUNNING.includes(q.state.data.status) ? 3000 : false),
	});
	const status = operation.data?.status;
	const file: GeneratedFile | undefined = operation.data?.output?.file;
	const failed = status === "failed" || status === "skipped";
	const locked = disabled || !editor || busy;
	const submitted = !!operationId;

	async function generate() {
		if (locked || submitted || !selected || !prompt.trim()) return;
		setBusy(true);
		setError("");
		try {
			const op = await generateMedia(brandId, requestId.current, {
				modality,
				model,
				prompt: prompt.trim(),
				preset,
				...(modality === "video" ? { seconds: length } : {}),
				...(modality === "speech" && voice ? { voice } : {}),
				...(Object.keys(knobs).length ? { params: knobs } : {}),
			});
			setOperationId(op.id);
		} catch (e) {
			setError(
				e instanceof ApiError
					? e.localized(locale)
					: tr(
							"Submission could not be confirmed. Retrying checks the same request without paying twice.",
							"暂时无法确认提交结果，再次提交会检查同一次请求，不会重复扣费。",
						),
			);
		} finally {
			setBusy(false);
		}
	}

	function reset() {
		setOperationId("");
		requestId.current = crypto.randomUUID();
		setError("");
	}

	const primary = {
		label: file
			? onUse
				? tr("Use asset", "使用素材")
				: tr("Generate another", "再生成一个")
			: failed
				? tr("Start a new generation", "开始新的生成")
				: submitted
					? tr("Generating…", "生成中…")
					: tr("Generate", "生成"),
		disabled: locked || (!file && !failed && (submitted || !selected || !prompt.trim())),
		onClick: () => {
			if (locked) return;
			if (failed) return reset();
			if (!file) return void generate();
			// What the picture shows is the first draft of its alt text.
			onUse?.(file.id, modality === "image" ? prompt.trim() : "", file.content_type);
			reset();
		},
	};
	return {
		t,
		tr,
		locale,
		kinds,
		modality,
		setModality,
		prompt,
		setPrompt,
		model,
		setModel: chooseModel,
		preset,
		setPreset,
		lengths,
		length,
		setSeconds,
		voice,
		setVoice,
		knobs,
		setKnobs,
		options,
		models,
		selected,
		locked,
		submitted,
		error,
		operation,
		status,
		failed,
		file,
		primary,
	};
}

function Fields({ g, settingsOnly = false }: { g: Generator; settingsOnly?: boolean }) {
	const { tr, locale } = g;
	return (
		<fieldset className="bare-fieldset stack" disabled={g.locked || g.submitted || !!g.error}>
			{g.kinds.length > 1 && (
				<ChipModalField type="custom" title={tr("Asset type", "素材类型")}>
					<ChipSelect
						aria-label={tr("Asset type", "素材类型")}
						value={g.modality}
						onChange={(value) => {
							g.setModality(value as Modality);
							g.setModel("");
						}}
						options={g.kinds.map((m) => ({ value: m, label: pick(locale, NAMES[m]) }))}
					/>
				</ChipModalField>
			)}
			{settingsOnly ? null : (
				<ChipModalField
					type="textarea"
					title={pick(locale, BRIEFS[g.modality])}
					value={g.prompt}
					onChange={g.setPrompt}
					maxLength={4000}
					rows={4}
					placeholder={
						g.modality === "speech"
							? tr("Enter the exact narration.", "填写配音原文。")
							: tr("Describe what to generate.", "描述需要生成的内容。")
					}
				/>
			)}
			<ChipModalField type="custom" title={tr("Model", "模型")}>
				<ChipSelect
					searchable
					searchPlaceholder={tr("Search models", "搜索模型")}
					aria-label={tr("Model", "模型")}
					value={g.model}
					onChange={g.setModel}
					disabled={g.locked || g.submitted || g.options.isPending}
					placeholder={tr("Choose a model", "选择模型")}
					options={g.models.map((m) => ({ value: m.id, label: m.name || m.id }))}
				/>
			</ChipModalField>
			{g.modality !== "speech" && (
				<ChipModalField type="custom" title={tr("Aspect ratio", "画面比例")}>
					<ChipSelect
						aria-label={tr("Aspect ratio", "画面比例")}
						value={g.preset}
						onChange={g.setPreset}
						disabled={g.locked || g.submitted}
						options={presets.presets.map((p) => ({
							value: p.id,
							label: `${pick(locale, p.label)} · ${aspectRatio(p.width, p.height)}`,
						}))}
					/>
				</ChipModalField>
			)}
			{g.modality === "video" && (
				<ChipModalField type="custom" title={tr("Length", "时长")}>
					<ChipSelect
						aria-label={tr("Length", "时长")}
						value={String(g.length)}
						onChange={(value) => g.setSeconds(Number(value))}
						disabled={g.locked || g.submitted}
						options={g.lengths.map((s) => ({ value: String(s), label: tr(`${s} seconds`, `${s} 秒`) }))}
					/>
				</ChipModalField>
			)}
			{g.modality === "speech" && !!g.selected?.voices.length && (
				<ChipModalField type="custom" title={tr("Voice", "音色")}>
					<ChipSelect
						searchable
						aria-label={tr("Voice", "音色")}
						value={g.voice || g.selected.voices[0]}
						onChange={g.setVoice}
						disabled={g.locked || g.submitted}
						options={g.selected.voices.map((v) => ({ value: v, label: v }))}
					/>
				</ChipModalField>
			)}
			{/* ponytail: only choice knobs here; ranges and seeds are set from a workflow node or the agent. */}
			{extraKnobs(g.selected).map(([key, knob]) => {
				const values =
					knob.type === "enum" ? knob.values : key !== "seed" && knob.type === "boolean" ? [true, false] : null;
				if (!values) return null;
				const title = KNOBS[key] ? pick(locale, KNOBS[key]) : key;
				const label = (v: string | number | boolean) =>
					v === true ? tr("On", "开") : v === false ? tr("Off", "关") : String(v);
				return (
					<ChipModalField key={key} type="custom" title={title}>
						<ChipSelect
							aria-label={title}
							value={key in g.knobs ? String(g.knobs[key]) : ""}
							onChange={(value) => {
								const { [key]: _, ...rest } = g.knobs;
								const picked = values.find((v) => String(v) === value);
								g.setKnobs(picked === undefined ? rest : { ...rest, [key]: picked });
							}}
							disabled={g.locked || g.submitted}
							placeholder={tr("Model default", "模型默认")}
							options={[
								{ value: "", label: tr("Model default", "模型默认") },
								...values.map((v) => ({ value: String(v), label: label(v) })),
							]}
						/>
					</ChipModalField>
				);
			})}
		</fieldset>
	);
}

function Status({ g }: { g: Generator }) {
	const { tr, t } = g;
	const rate = g.selected && {
		en: `Uses credits at this model's rate: ${g.selected.credits_per_1000_input.toFixed(2)} / ${g.selected.credits_per_1000_output.toFixed(2)} per 1k input/output ${g.modality === "speech" ? "characters" : g.modality === "video" ? "seconds" : "units"}.`,
		zh: `按模型实际用量扣费：每千输入 / 输出${g.modality === "speech" ? "字符" : g.modality === "video" ? "秒" : "单位"} ${g.selected.credits_per_1000_input.toFixed(2)} / ${g.selected.credits_per_1000_output.toFixed(2)} 积分。`,
	};
	return (
		<>
			{rate && <p className="hint">{tr(rate.en, rate.zh)}</p>}
			{g.selected?.tiered && (
				<p className="hint">
					{tr(
						"Higher resolution or generated audio bills at a multiple of this rate; left at the model default, the cheapest is used.",
						"更高分辨率或生成音频按该费率的倍数计费；保持模型默认时按最便宜的档位生成。",
					)}
				</p>
			)}
			{g.options.isPending && <p role="status">{tr("Loading models…", "正在加载模型…")}</p>}
			{!g.options.isPending && !g.options.error && !g.models.length && (
				<p role="status">{tr("No model of this type is available right now.", "当前没有这一类型的可用模型。")}</p>
			)}
			{g.options.error && (
				<p role="alert">{tr("Could not load models. Reopen to retry.", "模型加载失败，请重新打开重试。")}</p>
			)}
			{g.error && <p role="alert">{g.error}</p>}
			{g.operation.error && (
				<p role="alert">
					{tr("Could not refresh generation status. Reopen to check again.", "生成状态刷新失败，请重新打开查看。")}
				</p>
			)}
			{g.submitted && !g.file && !g.failed && (
				<p role="status">
					{g.status === "running" ? t("tasks.running") : tr("Waiting for generation…", "正在等待生成…")}
				</p>
			)}
			{g.file && <Preview file={g.file} alt={g.prompt} />}
			{g.failed && (
				<p role="alert">
					{g.operation.data?.error_code
						? t(`errors.${g.operation.data.error_code}`)
						: tr("Generation stopped before it produced a file.", "生成在产出文件之前停止了。")}
				</p>
			)}
		</>
	);
}

function Preview({ file, alt }: { file: GeneratedFile; alt: string }) {
	const src = fileUrl(file.id);
	if (file.content_type.startsWith("video/"))
		// biome-ignore lint/a11y/useMediaCaption: a generated clip has no track to offer yet.
		return <video className="w-full" src={src} controls />;
	if (file.content_type.startsWith("audio/"))
		// biome-ignore lint/a11y/useMediaCaption: narration is the caption's own source text.
		return <audio className="w-full" src={src} controls />;
	return <img className="w-full" src={src} alt={alt} />;
}

/** Generate a saved asset for a screen that only wants the file. */
export function GenerateMediaDialog({
	open,
	onOpenChange,
	title,
	...props
}: GeneratorProps & { open: boolean; onOpenChange: (open: boolean) => void; title?: string }) {
	const g = useGenerator(props, open);
	const heading = title ?? g.tr("Generate media", "生成素材");
	return (
		<ChipModal open={open} onOpenChange={onOpenChange} srTitle={heading}>
			<ChipModalHeader onClose={() => onOpenChange(false)} closeAriaLabel={g.t("common.close")}>
				{heading}
			</ChipModalHeader>
			<ChipModalBody>
				<Fields g={g} />
				<ChipModalField type="custom" title="">
					<Status g={g} />
				</ChipModalField>
			</ChipModalBody>
			<ChipModalFooter
				cancelLabel={g.t("common.close")}
				onCancel={() => onOpenChange(false)}
				defaultAction="none"
				primaryAction={{
					...g.primary,
					onClick: () => {
						const used = !!g.file;
						g.primary.onClick();
						if (used) onOpenChange(false);
					},
				}}
			/>
		</ChipModal>
	);
}

/**
 * Choose a model and its knobs for the Agent to generate with, without
 * generating: the same fields as the generator, less the prompt, which is the
 * Agent's to write.
 */
export function MediaSettingsDialog({
	brandId,
	initial,
	open,
	onOpenChange,
	onApply,
}: {
	brandId: string;
	initial: MediaSettings;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onApply: (settings: MediaSettings, label: string) => void;
}) {
	const g = useGenerator({ brandId, modalities: [initial.modality], initial }, open);
	const { tr, locale } = g;
	const heading = tr("Generation settings", "生成设置");
	const voice = g.modality === "speech" ? g.voice || g.selected?.voices[0] : undefined;
	const settings: MediaSettings = {
		modality: g.modality,
		model: g.model,
		...(g.modality === "speech" ? {} : { preset: g.preset }),
		...(g.modality === "video" ? { seconds: g.length } : {}),
		...(voice ? { voice } : {}),
		...(Object.keys(g.knobs).length ? { params: g.knobs as MediaSettings["params"] } : {}),
	};
	const preset = presets.presets.find((p) => p.id === settings.preset);
	const label = [
		g.selected?.name || g.model,
		preset ? pick(locale, preset.label) : null,
		settings.seconds ? tr(`${settings.seconds}s`, `${settings.seconds} 秒`) : null,
		voice ?? null,
		...Object.entries(settings.params ?? {}).map(([key, v]) =>
			typeof v === "boolean"
				? `${KNOBS[key] ? pick(locale, KNOBS[key]) : key} ${v ? tr("on", "开") : tr("off", "关")}`
				: String(v),
		),
	]
		.filter(Boolean)
		.join(" · ");
	return (
		<ChipModal open={open} onOpenChange={onOpenChange} srTitle={heading}>
			<ChipModalHeader onClose={() => onOpenChange(false)} closeAriaLabel={g.t("common.close")}>
				{heading}
			</ChipModalHeader>
			<ChipModalBody>
				<Fields g={g} settingsOnly />
				<ChipModalField type="custom" title="">
					<p className="hint">
						{tr(
							"The Agent generates with this model and these values for this message. A knob left at the model default is the Agent's to choose.",
							"这条消息里，智能体会用这个模型和这些设置生成。保持「模型默认」的参数由智能体决定。",
						)}
					</p>
					<Status g={g} />
				</ChipModalField>
			</ChipModalBody>
			<ChipModalFooter
				cancelLabel={g.t("common.cancel")}
				onCancel={() => onOpenChange(false)}
				primaryAction={{
					label: tr("Use these settings", "使用这些设置"),
					disabled: !g.selected,
					onClick: () => {
						onApply(settings, label);
						onOpenChange(false);
					},
				}}
			/>
		</ChipModal>
	);
}

function aspectRatio(width: number, height: number): string {
	let a = width;
	let b = height;
	while (b) [a, b] = [b, a % b];
	return `${width / a}:${height / a}`;
}
